<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDialog, type FormInst, type FormRules } from 'naive-ui';
import { IconLibrary } from '@/components/design/icons';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import { ExtractionPromptsService } from '@/services/contractManagement/extractionPromptsService';
import {
  ContractManagementColumnExtractionScope,
  type ExtractionPromptInDb,
  type ExtractionPromptQuestion,
  type ExtractionPromptSearchEntry,
  type ExtractionPromptVersionInDb,
  type AndCriterionInput,
  type EnumResponseType,
  type EnumResponseTypeValue,
} from '@/api';
import RecursiveQueryBuilder from './RecursiveQueryBuilder.vue';
import PromptSelectionModal from './PromptSelectionModal.vue';
import ExtractionScopeModal from './ExtractionScopeModal.vue';
import QuestionFields from './QuestionFields.vue';
import {
  validateFilters,
  hasActiveFilters,
  normalizeFilterFromApi,
} from '@/utils/contractManagementView/staticFilters';
import type { ResponseType } from '@/utils/contractManagementView/filterValueValidation';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import { logger } from '@/utils/logger';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import type { LinkedPrompt, PreviewColumnData } from '@/types/contractManagement/columnPreview';
import {
  columnQuestion,
  enumOptionsError,
  formToQuestion,
  isEnumResponseType,
  promptToForm,
  sameQuestion,
} from '@/utils/contractManagementView/columnQuestion';

interface Props {
  show: boolean;
  /** Column UID for persisted mode (required when previewMode is false) */
  columnUid?: string;
  filterOptions?: { label: string; value: string | number }[];
  initialTab?: string;
  /** If true, works with local PreviewColumnData instead of API */
  previewMode?: boolean;
  /** Column data for preview mode (required when previewMode is true) */
  previewColumn?: PreviewColumnData | null;
  /** Persisted mode: the column's view, so the prompt picker marks the prompts it already asks. */
  viewUid?: string;
  /** Preview mode: the prompts the wizard's columns already link. */
  usedPromptUids?: string[];
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  /** Emitted after successful API update (persisted mode) */
  (e: 'updated'): void;
  /** Emitted after successful API delete (persisted mode) */
  (e: 'deleted'): void;
  /** Emitted with updated column data (preview mode) */
  (e: 'preview-updated', column: PreviewColumnData): void;
  /** Emitted when column is deleted (preview mode) */
  (e: 'preview-deleted', columnId: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  columnUid: '',
  filterOptions: () => [],
  initialTab: 'settings',
  previewMode: false,
  previewColumn: null,
  viewUid: undefined,
  usedPromptUids: () => [],
});
const emit = defineEmits<Emits>();
const message = useGaiaToast();
const contractTypeStore = useContractTypeStore();
const dialog = useDialog();

const isPreviewMode = computed(() => props.previewMode);

// Control drawer visibility
const showDrawer = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const formRef = ref<FormInst | null>(null);
const saving = ref(false);
const deleting = ref(false);
const loading = ref(false);
const hasFilterValidationErrors = ref(false);

// Flag to distinguish programmatically setting data vs user input
const isInitializing = ref(false);

// Re-extraction scope prompt (persisted mode, only when the question itself changed)
const showExtractionScopeModal = ref(false);
// Question history state: the versions of the column's prompt, newest first
const questionHistory = ref<ExtractionPromptVersionInDb[]>([]);
const currentQuestionUid = ref<string | null>(null);
/** Persisted mode: the prompt the column asks; a changed question becomes its next version. */
const currentPrompt = ref<ExtractionPromptInDb | null>(null);

// The existing prompt the column asks: its own, or one picked from a contract type. Linked only
// while the form still asks its question (see linkedPrompt).
const pickedPrompt = ref<LinkedPrompt | null>(null);
const showPromptSelectionModal = ref(false);
/** A new question's contract types, so other views can pick it from them. */
const newPromptContractTypeUids = ref<string[]>([]);
const isLoadingHistory = ref(false);
const showHistory = ref(false);

// Active tab state
const activeTab = ref<string>(props.initialTab || 'settings');

// Watch for prop changes to update active tab
watch(
  () => props.initialTab,
  newTab => {
    if (newTab) {
      activeTab.value = newTab;
    }
  }
);

// Hide history panel when switching away from question tab
watch(activeTab, newTab => {
  if (newTab !== 'question') {
    showHistory.value = false;
  }
});

// Form Data Model
const formData = ref<{
  name: string;
  visible: boolean;
  prompt: string;
  responseType: string;
  exampleData: string;
  filters: AndCriterionInput | null;
  enumOptions: EnumResponseTypeValue[];
}>({
  name: '',
  visible: true,
  prompt: '',
  responseType: 'str',
  exampleData: '',
  filters: null,
  enumOptions: [],
});

// Store original form data to detect changes
const originalFormData = ref<{
  name: string;
  visible: boolean;
  prompt: string;
  responseType: string;
  exampleData: string;
  filters: AndCriterionInput | null;
  enumOptions: EnumResponseTypeValue[];
}>({
  name: '',
  visible: true,
  prompt: '',
  responseType: 'str',
  exampleData: '',
  filters: null,
  enumOptions: [],
});

// QuestionFields takes the required prompt's message as a model rather than an n-form rule.
const promptError = ref<string>('');

const isEnumType = computed(() => isEnumResponseType(formData.value.responseType));

// Computed property for enum response type to avoid recreating object on every render
const enumResponseType = computed((): EnumResponseType | undefined => {
  if (!isEnumType.value) return undefined;
  return {
    multiple_choices: formData.value.responseType === 'multiple_enum',
    values: formData.value.enumOptions,
  };
});

// A persisted column without a name shows its prompt's display name; preview columns always have one.
const formRules = computed((): FormRules => ({
  name: {
    required: isPreviewMode.value,
    message: 'Please enter column name',
    trigger: ['blur', 'input'],
  },
}));

// What a column without a name shows: its prompt's display name.
const promptDisplayName = computed(
  (): string | undefined => linkedPrompt.value?.display_name ?? currentPrompt.value?.display_name
);

// Separate helper to reset form to defaults
function resetForm(): void {
  formData.value = {
    name: '',
    visible: true,
    prompt: '',
    responseType: 'str',
    exampleData: '',
    filters: null,
    enumOptions: [],
  };
  originalFormData.value = {
    name: '',
    visible: true,
    prompt: '',
    responseType: 'str',
    exampleData: '',
    filters: null,
    enumOptions: [],
  };
  questionHistory.value = [];
  currentQuestionUid.value = null;
  currentPrompt.value = null;
  pickedPrompt.value = null;
  newPromptContractTypeUids.value = [];
  showHistory.value = false;
  promptError.value = '';
}

// Editing the picked prompt's question makes it a new question; undoing the edit links it again.
const linkedPrompt = computed((): LinkedPrompt | null =>
  pickedPrompt.value && sameQuestion(formData.value, pickedPrompt.value) ? pickedPrompt.value : null
);

// Load the prompt's versions (lazy loading - only when user requests it)
async function loadQuestionHistory(promptUid: string): Promise<void> {
  if (!promptUid) return;

  // Skip if already loaded (avoid duplicate requests)
  if (questionHistory.value.length > 0 && !isLoadingHistory.value) return;

  isLoadingHistory.value = true;
  try {
    questionHistory.value = await ExtractionPromptsService.searchVersions(promptUid);
  } catch (error) {
    // Silently fail - history is not critical, but log for debugging
    logger.error('Failed to load question history:', error);
    questionHistory.value = [];
  } finally {
    isLoadingHistory.value = false;
  }
}

// Handle showing history - load it lazily if not already loaded
async function handleShowHistory(): Promise<void> {
  // Load history if not already loaded
  if (questionHistory.value.length === 0 && !isLoadingHistory.value && currentQuestionUid.value) {
    await loadQuestionHistory(currentQuestionUid.value);
  }
  showHistory.value = true;
}

// Load question data into form
function loadQuestionIntoForm(question: ExtractionPromptQuestion): void {
  Object.assign(formData.value, promptToForm(question));
}

// Link an existing prompt from a contract type. Saving switches the column to it.
function linkPrompt(prompt: LinkedPrompt): void {
  loadQuestionIntoForm(prompt);
  pickedPrompt.value = prompt;
}

function handlePromptSelected(prompt: ExtractionPromptSearchEntry): void {
  linkPrompt(prompt);
}

// Update original form data snapshot
function updateOriginalFormData(): void {
  originalFormData.value = {
    name: formData.value.name,
    visible: formData.value.visible,
    prompt: formData.value.prompt,
    responseType: formData.value.responseType,
    exampleData: formData.value.exampleData,
    filters: formData.value.filters ? JSON.parse(JSON.stringify(formData.value.filters)) : null,
    enumOptions: JSON.parse(JSON.stringify(formData.value.enumOptions)),
  };
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Check if a version's question is the one the form asks
const isSelectedQuestion = computed(() => (version: ExtractionPromptVersionInDb): boolean => {
  return sameQuestion(formData.value, version);
});

/** A changed question that saving makes the next version of the column's prompt. */
const savesNewVersion = computed(
  (): boolean => !isPreviewMode.value && !linkedPrompt.value && hasQuestionDataChanges.value
);

// Helper function to compare example data (handles JSON parsing)
function compareExampleData(current: string, original: string): boolean {
  const currentTrimmed = current.trim();
  const originalTrimmed = original.trim();

  if (currentTrimmed === originalTrimmed) return false;

  // Try to parse both as JSON and compare
  try {
    const currentParsed = currentTrimmed ? JSON.parse(currentTrimmed) : null;
    const originalParsed = originalTrimmed ? JSON.parse(originalTrimmed) : null;
    return JSON.stringify(currentParsed) !== JSON.stringify(originalParsed);
  } catch {
    // If parsing fails, compare as strings
    return true;
  }
}

// Check if column metadata (name, visible, filters) has changed
const hasColumnMetadataChanges = computed((): boolean => {
  const filtersChanged =
    JSON.stringify(formData.value.filters) !== JSON.stringify(originalFormData.value.filters);
  return (
    formData.value.name.trim() !== originalFormData.value.name.trim() ||
    formData.value.visible !== originalFormData.value.visible ||
    filtersChanged
  );
});

// Check if question data has changed
const hasQuestionDataChanges = computed((): boolean => {
  return (
    formData.value.prompt.trim() !== originalFormData.value.prompt.trim() ||
    formData.value.responseType !== originalFormData.value.responseType ||
    compareExampleData(formData.value.exampleData, originalFormData.value.exampleData) ||
    JSON.stringify(formData.value.enumOptions) !==
      JSON.stringify(originalFormData.value.enumOptions)
  );
});

const isEnumValid = computed((): boolean => enumOptionsError(formData.value) === null);

// Whether saving changes the column's question: another linked prompt, or a new question
const hasQuestionChanges = computed((): boolean =>
  linkedPrompt.value
    ? linkedPrompt.value.uid !== currentQuestionUid.value
    : hasQuestionDataChanges.value
);

// Check if form has been modified (any changes)
const hasFormChanges = computed((): boolean => {
  return hasColumnMetadataChanges.value || hasQuestionChanges.value;
});

// Check if there are multiple questions in history
const hasMultipleQuestions = computed((): boolean => {
  return questionHistory.value.length > 1;
});

// Dynamic drawer width based on history visibility and active tab
const drawerWidth = computed((): number => {
  if (activeTab.value === 'filters') {
    return 1000;
  }
  return showHistory.value && hasMultipleQuestions.value ? 1000 : 600;
});

async function loadColumnData(uid: string): Promise<void> {
  // If no UID is provided (or it's empty), stop here.
  if (!uid) return;

  loading.value = true;
  isInitializing.value = true;

  try {
    // Fetch specific column data
    // Note: Question history is loaded lazily only when user clicks "Show Prompt History"
    // This avoids unnecessary API calls when opening the drawer
    const column = await ContractViewsService.getColumnById(uid);

    // 3. Populate Form
    formData.value.name = column.name ?? '';
    formData.value.visible = column.visible;

    // Normalize filters from API format (AndCriterionOutput) to internal format (AndCriterionInput)
    // This converts criterion_type to type if needed
    if (column.filters.criteria.length > 0) {
      const normalized = normalizeFilterFromApi(column.filters);
      formData.value.filters = normalized as AndCriterionInput | null;
    } else {
      formData.value.filters = null;
    }

    const prompt = column.extraction_prompt;
    loadQuestionIntoForm(prompt);
    currentQuestionUid.value = prompt.uid;
    currentPrompt.value = prompt;
    pickedPrompt.value = prompt;

    // Store original form data after loading
    updateOriginalFormData();
  } catch {
    message.error('Failed to load column data');
    // If loading fails, close drawer to prevent stuck state
    showDrawer.value = false;
  } finally {
    loading.value = false;
    setTimeout(() => {
      isInitializing.value = false;
    }, 100);
  }
}

// Load preview column data into form (for preview mode)
function loadPreviewColumnData(column: PreviewColumnData): void {
  isInitializing.value = true;

  formData.value.name = column.name;
  formData.value.visible = column.visible;
  formData.value.filters = column.filters ?? null;

  if (column.question) {
    loadQuestionIntoForm(column.question);
  } else {
    // Fallbacks if no question set
    formData.value.prompt = '';
    formData.value.responseType = 'str';
    formData.value.exampleData = '';
    formData.value.enumOptions = [];
  }

  // No question history in preview mode
  questionHistory.value = [];
  currentQuestionUid.value = column.linkedPrompt?.uid ?? null;
  pickedPrompt.value = column.linkedPrompt ?? null;
  newPromptContractTypeUids.value = column.contractTypeUids ?? [];
  showHistory.value = false;

  updateOriginalFormData();

  setTimeout(() => {
    isInitializing.value = false;
  }, 100);
}

// Watch for drawer open - handle both preview and persisted modes
watch(
  [
    (): boolean => props.show,
    (): string => props.columnUid || '',
    (): PreviewColumnData | null => props.previewColumn,
  ],
  async ([isShow, newUid, previewCol], [wasShow, oldUid]): Promise<void> => {
    if (!isShow) return;
    void contractTypeStore.ensureLoaded();

    if (isPreviewMode.value) {
      // Preview mode: load from previewColumn prop
      if (previewCol && (!wasShow || previewCol.id !== (props.previewColumn?.id ?? ''))) {
        resetForm();
        loadPreviewColumnData(previewCol);
      }
    } else {
      // Persisted mode: load from API using columnUid
      if (newUid && (!wasShow || newUid !== oldUid)) {
        if (!wasShow) resetForm();
        await loadColumnData(newUid);
      }
    }
  },
  { immediate: true }
);

// Build question data from form
function buildQuestionData(): ExtractionPromptQuestion {
  return formToQuestion(formData.value);
}

async function handleSave(): Promise<void> {
  promptError.value = formData.value.prompt.trim() ? '' : 'Please enter a prompt or question';
  const fieldsValid = await (formRef.value?.validate().then(
    () => true,
    () => false
  ) ?? true);
  if (!fieldsValid || promptError.value) {
    message.error('Please fill in all required fields');
    return;
  }

  if (!hasFormChanges.value) {
    showDrawer.value = false;
    return;
  }

  const optionsError = enumOptionsError(formData.value);
  if (optionsError) {
    message.error(optionsError);
    return;
  }

  // Validate filters
  if (formData.value.filters) {
    const validation = validateFilters(formData.value.filters);
    if (!validation.valid) {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      message.error(`Invalid filter structure: ${validation.error || 'Unknown error'}`);
      return;
    }
  }

  if (isPreviewMode.value) {
    if (!props.previewColumn) {
      logger.error('previewColumn is required in preview mode');
      return;
    }

    // Preview mode: emit updated column data
    const questionData = buildQuestionData();

    const updatedColumn: PreviewColumnData = {
      id: props.previewColumn.id,
      name: formData.value.name.trim(),
      visible: formData.value.visible,
      order_index: props.previewColumn.order_index,
      question: questionData,
      linkedPrompt: linkedPrompt.value,
      contractTypeUids: newPromptContractTypeUids.value,
      promptName: props.previewColumn.promptName,
      filters: formData.value.filters,
    };

    emit('preview-updated', updatedColumn);
    message.success('Column updated');
    showDrawer.value = false;
    return;
  }

  // A changed question re-runs extractions, so the user has to choose how much of the existing
  // backlog to re-extract before we can save.
  if (hasQuestionChanges.value) {
    showExtractionScopeModal.value = true;
    return;
  }

  await persistChanges(null);
}

/**
 * Persist the column changes (persisted mode only).
 * @param scope How many existing contracts to re-extract with the new question. Required whenever
 * the question changed - the backend has no default; `null` only for metadata-only saves.
 */
async function persistChanges(
  scope: ContractManagementColumnExtractionScope | null
): Promise<void> {
  const questionChanged = hasQuestionChanges.value;

  saving.value = true;
  try {
    const updatePromises: Promise<unknown>[] = [];

    // Only update column metadata if it has changed
    if (hasColumnMetadataChanges.value) {
      const filtersToSend = formData.value.filters ?? { type: 'and', criteria: [] };

      updatePromises.push(
        ContractViewsService.updateColumn(props.columnUid, {
          // None: the column shows, and follows, its prompt's display name.
          name: formData.value.name.trim() || null,
          visible: formData.value.visible,
          filters: filtersToSend,
        })
      );
    }

    // Only update question data if it has changed
    if (questionChanged && scope) {
      updatePromises.push(
        ContractViewsService.updateColumnQuestion(
          props.columnUid,
          // A new question is the next version of the column's prompt, which keeps its types.
          columnQuestion(buildQuestionData(), linkedPrompt.value),
          scope
        )
      );
    }

    // Execute all necessary updates in parallel
    await Promise.all(updatePromises);

    // Update original form data after successful save
    updateOriginalFormData();

    message.success(
      questionChanged && scope && scope !== ContractManagementColumnExtractionScope.NEW_ONLY
        ? 'Column updated - re-extraction started in the background'
        : 'Column updated successfully'
    );
    emit('updated');
    showDrawer.value = false;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to update column';
    message.error(errorMsg);
  } finally {
    saving.value = false;
  }
}

async function handleConfirmExtractionScope(
  scope: ContractManagementColumnExtractionScope
): Promise<void> {
  showExtractionScopeModal.value = false;
  await persistChanges(scope);
}

function handleCancel(): void {
  showDrawer.value = false;
}

function handleDelete(): void {
  const deleteMessage = isPreviewMode.value
    ? 'Are you sure you want to remove this column from the preview?'
    : 'Are you sure you want to delete this column? This action cannot be undone.';

  dialog.warning({
    title: 'Delete Column',
    content: deleteMessage,
    positiveText: 'Delete',
    negativeText: 'Cancel',
    showIcon: false,
    positiveButtonProps: {
      type: 'error',
    },
    onPositiveClick: async () => {
      if (isPreviewMode.value) {
        if (!props.previewColumn) {
          logger.error('previewColumn is required in preview mode');
          return;
        }

        // Preview mode: emit delete event
        emit('preview-deleted', props.previewColumn.id);
        message.success('Column removed');
        showDrawer.value = false;
        return;
      }

      // Persisted mode: delete via API
      deleting.value = true;
      try {
        await ContractViewsService.deleteColumn(props.columnUid);
        message.success('Column deleted successfully');
        emit('deleted');
        showDrawer.value = false;
      } catch {
        message.error('Failed to delete column');
      } finally {
        deleting.value = false;
      }
    },
  });
}
</script>

<template>
  <NDrawer v-model:show="showDrawer" :width="drawerWidth" placement="right">
    <NDrawerContent
      title="Edit Column"
      closable
      data-tour="views.edit-drawer"
      @close="handleCancel"
    >
      <div v-if="loading" class="loading-state">
        <p>Loading column details...</p>
      </div>

      <div v-else class="drawer-content-wrapper">
        <!-- Left Panel: Prompt History (when expanded and on Question tab) -->
        <div
          v-if="!isPreviewMode && showHistory && hasMultipleQuestions && activeTab === 'question'"
          class="history-panel"
        >
          <div class="history-panel-header">
            <NText strong style="font-size: 16px">Prompt History</NText>
            <NButton text size="small" type="primary" @click="showHistory = false"> Hide </NButton>
          </div>
          <NDivider style="margin: 12px 0" />
          <NScrollbar class="history-scrollbar">
            <NTimeline>
              <NTimelineItem
                v-for="question in questionHistory"
                :key="question.uid"
                :type="question.is_current ? 'success' : 'default'"
                :time="formatDate(question.created_at)"
              >
                <NCard
                  :bordered="isSelectedQuestion(question)"
                  :style="{
                    cursor: 'pointer',
                    opacity: isSelectedQuestion(question) ? 1 : 0.7,
                    borderColor: isSelectedQuestion(question) ? '#18a058' : undefined,
                    backgroundColor: isSelectedQuestion(question) ? '#f6ffed' : undefined,
                    transition: 'all 0.2s',
                  }"
                  size="small"
                  hoverable
                  @click="loadQuestionIntoForm(question)"
                >
                  <NSpace vertical :size="4">
                    <div style="display: flex; justify-content: space-between; align-items: start">
                      <NText
                        :strong="isSelectedQuestion(question)"
                        :depth="isSelectedQuestion(question) ? 1 : 3"
                        style="flex: 1; word-break: break-word; font-size: 13px"
                      >
                        {{ question.prompt || '(No prompt)' }}
                      </NText>
                      <NText
                        v-if="question.is_current"
                        type="success"
                        depth="3"
                        style="margin-left: 8px; white-space: nowrap; font-size: 11px"
                      >
                        Current
                      </NText>
                    </div>
                    <NDivider style="margin: 8px 0" />
                    <NSpace vertical :size="4" style="font-size: 12px; color: #6b7280">
                      <div><strong>Version:</strong> {{ question.version }}</div>
                      <div><strong>Type:</strong> {{ question.response_type }}</div>
                    </NSpace>
                  </NSpace>
                </NCard>
              </NTimelineItem>
            </NTimeline>
          </NScrollbar>
        </div>

        <!-- Divider between panels -->
        <NDivider
          v-if="!isPreviewMode && showHistory && hasMultipleQuestions && activeTab === 'question'"
          vertical
          class="panel-divider"
        />

        <!-- Right Panel: Form Content with Tabs -->
        <div class="form-panel">
          <NTabs v-model:value="activeTab" type="line" animated>
            <NTabPane name="settings" tab="Column Settings">
              <NForm
                ref="formRef"
                :model="formData"
                :rules="formRules"
                label-placement="top"
                style="margin-top: 16px"
              >
                <NFormItem label="Column Name" path="name" :required="isPreviewMode">
                  <gaia-input
                    v-model:value="formData.name"
                    :placeholder="
                      isPreviewMode
                        ? 'Enter column name'
                        : promptDisplayName || 'Same as the prompt name'
                    "
                    :maxlength="200"
                  />
                </NFormItem>

                <gaia-toggle-row
                  card
                  :style="{ marginBottom: '18px' }"
                  label="Visible"
                  :hint="
                    isPreviewMode
                      ? 'Show this column in the preview'
                      : 'Show this column in the grid'
                  "
                  :checked="formData.visible"
                  @update:checked="(value: boolean) => (formData.visible = value)"
                />
              </NForm>
            </NTabPane>

            <NTabPane name="question" tab="Question">
              <div class="question-tab-content">
                <!-- Question Form -->
                <div class="question-form-panel">
                  <NForm
                    ref="formRef"
                    :model="formData"
                    :rules="formRules"
                    label-placement="top"
                    style="margin-top: 16px"
                  >
                    <n-flex vertical :size="10" :wrap="false" :style="{ marginBottom: '18px' }">
                      <gaia-button block @click="showPromptSelectionModal = true">
                        <template #icon>
                          <NIcon :component="IconLibrary" />
                        </template>
                        Use a prompt from a contract type
                      </gaia-button>
                      <gaia-banner v-if="savesNewVersion && currentPrompt" type="warning">
                        Saving makes this question the next version of
                        <strong>{{ currentPrompt.display_name }}</strong
                        >. Every column that asks it, in any view, switches to the new version.
                      </gaia-banner>
                      <gaia-banner v-else-if="linkedPrompt && isPreviewMode" type="info">
                        Asks <strong>{{ linkedPrompt.display_name }}</strong
                        >. Editing its question saves the column with a new prompt instead.
                      </gaia-banner>
                      <gaia-banner v-else-if="linkedPrompt" type="info">
                        Asks <strong>{{ linkedPrompt.display_name }}</strong
                        >. Editing its question saves a new version of the column's prompt.
                      </gaia-banner>
                    </n-flex>

                    <n-flex vertical :size="16" :wrap="false">
                      <question-fields v-model:prompt-error="promptError" :form="formData" />

                      <!-- Prompt history controls (only in persisted mode) -->
                      <template v-if="!isPreviewMode">
                        <div
                          v-if="currentQuestionUid && !showHistory"
                          style="
                            display: flex;
                            justify-content: flex-end;
                            align-items: center;
                            gap: 8px;
                          "
                        >
                          <NButton
                            text
                            type="primary"
                            size="small"
                            :loading="isLoadingHistory"
                            @click="handleShowHistory"
                          >
                            Show Prompt History
                            <span v-if="questionHistory.length > 0" style="margin-left: 4px"
                              >({{ questionHistory.length }})</span
                            >
                          </NButton>
                        </div>
                        <div
                          v-else-if="
                            showHistory &&
                            questionHistory.length === 1 &&
                            !loading &&
                            !isLoadingHistory
                          "
                          style="display: flex; justify-content: flex-end"
                        >
                          <NText depth="3" style="font-size: 12px"
                            >No previous questions found</NText
                          >
                        </div>
                        <div
                          v-else-if="
                            showHistory &&
                            questionHistory.length === 0 &&
                            !loading &&
                            !isLoadingHistory
                          "
                          style="display: flex; justify-content: flex-end"
                        >
                          <NText depth="3" style="font-size: 12px"
                            >No question history available</NText
                          >
                        </div>
                      </template>

                      <!-- Only a new question in the wizard becomes a new prompt; a saved column's
                           new question is a version of its prompt, which keeps its own types. -->
                      <gaia-form-field
                        v-if="isPreviewMode && !linkedPrompt"
                        label="Contract types"
                        hint="Other views pick the new prompt from these; leave empty for a standalone prompt"
                      >
                        <gaia-select
                          v-model:value="newPromptContractTypeUids"
                          multiple
                          filterable
                          :max-tag-count="3"
                          :options="contractTypeStore.uidOptions"
                          placeholder="No contract type"
                        />
                      </gaia-form-field>
                    </n-flex>
                  </NForm>
                </div>
              </div>
            </NTabPane>

            <NTabPane name="filters" tab="Filters">
              <NForm
                ref="formRef"
                :model="formData"
                :rules="formRules"
                label-placement="top"
                style="margin-top: 16px"
              >
                <NFormItem label="Filters" path="filters">
                  <div style="width: 100%">
                    <NText depth="3" style="font-size: 12px; margin-bottom: 12px; display: block">
                      Configure automatic filters for this column. Only rows matching these filters
                      will be displayed.
                    </NText>
                    <RecursiveQueryBuilder
                      v-if="
                        (filterOptions && filterOptions.length > 0) ||
                        hasActiveFilters(formData.filters) ||
                        true
                      "
                      v-model="formData.filters"
                      :available-options="filterOptions || []"
                      :response-type="formData.responseType as ResponseType"
                      :enum-response-type="enumResponseType"
                      :is-static-column="false"
                      placeholder="Enter filter values..."
                      @validation-error="hasFilterValidationErrors = $event"
                    />
                    <NText v-else depth="3" style="font-size: 12px; font-style: italic">
                      No filter options available. Filter options are populated from the data in
                      this view.
                    </NText>
                  </div>
                </NFormItem>
              </NForm>
            </NTabPane>
          </NTabs>
        </div>
      </div>

      <template #footer>
        <div class="footer-content">
          <NSpace justify="space-between" style="width: 100%">
            <NButton type="error" :loading="deleting" @click="handleDelete">Delete</NButton>
            <NSpace>
              <NButton @click="handleCancel">Cancel</NButton>
              <NButton
                type="primary"
                :loading="saving"
                :disabled="!hasFormChanges || hasFilterValidationErrors || !isEnumValid"
                @click="handleSave"
              >
                Update Column
              </NButton>
            </NSpace>
          </NSpace>
        </div>
      </template>
    </NDrawerContent>
  </NDrawer>

  <PromptSelectionModal
    v-model:show="showPromptSelectionModal"
    :view-uid="isPreviewMode ? undefined : viewUid"
    :used-prompt-uids="usedPromptUids"
    @select-prompt="handlePromptSelected"
  />

  <!-- Re-extraction scope: asked whenever a saved question changes -->
  <extraction-scope-modal
    v-model:show="showExtractionScopeModal"
    :loading="saving"
    :across-views="savesNewVersion"
    @confirm="handleConfirmExtractionScope"
  />
</template>

<style scoped>
:deep(.n-drawer-content__body) {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  min-height: 0;
}

:deep(.n-drawer-content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

:deep(.n-drawer-content__header) {
  flex-shrink: 0;
}

:deep(.n-drawer-content__footer) {
  flex-shrink: 0;
}

:deep(.n-form-item-label) {
  font-weight: 500;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.drawer-content-wrapper {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  height: 100%;
}

.history-panel {
  width: 380px;
  min-width: 380px;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  background-color: #f9fafb;
  padding: 20px;
  overflow: hidden;
  height: 100%;
  min-height: 0;
}

.history-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.history-panel-header + .n-divider {
  flex-shrink: 0;
}

.panel-divider {
  flex-shrink: 0;
  margin: 0 16px;
}

.history-scrollbar {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.history-scrollbar :deep(.n-scrollbar-container) {
  height: 100%;
  overflow: hidden;
}

.history-scrollbar :deep(.n-scrollbar-content) {
  padding-right: 8px;
  height: 100%;
}

.form-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}

.question-tab-content {
  display: flex;
  flex-direction: row;
  gap: 0;
  width: 100%;
  min-height: 0;
}

.question-form-panel {
  flex: 1;
  min-width: 0;
}

.footer-content {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 0;
}

.footer-content > :deep(.n-space) {
  width: 600px;
  max-width: 100%;
  margin-left: auto;
}

/* Smooth transition for drawer width */
:deep(.n-drawer) {
  transition: width 0.3s ease;
}

/* Timeline styling improvements */
:deep(.n-timeline-item__content) {
  margin-top: 8px;
}

:deep(.n-timeline-item__timeline) {
  margin-right: 12px;
}

/* Card hover effects */
:deep(.n-card) {
  transition: all 0.2s ease;
}

:deep(.n-card:hover) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
